import React, { useState, useEffect } from 'react';
import { auth, api } from './auth';
import MfaEnrollmentGate from './MfaEnrollmentGate';

const API = process.env.REACT_APP_API_URL || '';

export default function Login({ onAuth }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [platform, setPlatform] = useState({ system_name: 'Mandate Verifier', primary_colour: '#111827', has_logo: false, privacy_notice: '' });
  const [platformLoaded, setPlatformLoaded] = useState(false);

  const inviteMatch = window.location.pathname.match(/^\/invite\/([\w-]+)/);
  const inviteToken = inviteMatch ? inviteMatch[1] : null;

  const resetMatch = window.location.pathname.match(/^\/reset\/([\w-]+)/);
  const resetToken = resetMatch ? resetMatch[1] : null;

  const [inviteInfo, setInviteInfo] = useState(null);
  const [resetInfo, setResetInfo] = useState(null);
  const [fullName, setFullName] = useState('');

  useEffect(() => {
    api.get('/platform-settings')
      .then(r => { if (r.data && typeof r.data === 'object' && r.data.system_name) setPlatform(r.data); })
      .catch(() => {})
      .finally(() => setPlatformLoaded(true));
    if (inviteToken) {
      api.get(`/auth/check-invitation/${inviteToken}`).then(r => setInviteInfo(r.data)).catch(() => setError('Invalid or expired invitation'));
    } else if (resetToken) {
      api.get(`/auth/check-reset-token/${resetToken}`)
        .then(r => setResetInfo(r.data))
        .catch(err => setError(err.response?.data?.detail || 'This reset link is invalid or has expired'));
    }
  }, [inviteToken, resetToken]);

  // MFA challenge state
  const [mfaPendingToken, setMfaPendingToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  // Forced 2FA enrollment gate: only the token lives here; the gate component owns the rest.
  const [enrolToken, setEnrolToken] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      let user;
      if (inviteToken && inviteInfo) {
        const r = await api.post('/auth/accept-invitation', { token: inviteToken, password, full_name: fullName });
        auth.setSession(r.data.access_token, r.data.user);
        user = r.data.user;
        window.history.replaceState({}, '', '/');
        onAuth(user);
      } else if (resetToken && resetInfo) {
        if (!password || password.length < 8) {
          setError('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        const r = await api.post('/auth/complete-password-reset', { token: resetToken, new_password: password });
        auth.setSession(r.data.access_token, r.data.user);
        user = r.data.user;
        window.history.replaceState({}, '', '/');
        onAuth(user);
      } else {
        const result = await auth.login(email, password);
        if (result?.mfa_required) {
          setMfaPendingToken(result.mfa_pending_token);
          setLoading(false);
          return;  // wait for MFA submission
        }
        if (result?.mfa_enrollment_required) {
          setEnrolToken(result.enrol_pending_token);
          setLoading(false);
          return;  // hand off to the enrollment gate
        }
        onAuth(result);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Authentication failed');
    } finally { setLoading(false); }
  };

  const submitMFA = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const { user } = await auth.completeMFA(mfaPendingToken, mfaCode, useBackupCode);
      onAuth(user);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'MFA verification failed');
    } finally { setLoading(false); }
  };

  const cancelMFA = () => {
    setMfaPendingToken(null);
    setMfaCode('');
    setUseBackupCode(false);
    setPassword('');
    setError(null);
  };

  const inp = { width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, marginBottom: 12, fontFamily: 'inherit', outline: 'none' };

  // Hold the first paint until platform branding has resolved, so the card does not
  // flash from the default name, colour, and logo to the real ones (the flicker that
  // reads as two login screens). On error the fetch still resolves the gate.
  if (!platformLoaded) {
    return <div style={{ minHeight: '100vh', background: '#F8F9FB' }} />;
  }

  if (inviteToken && !inviteInfo && !error) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p>Loading invitation…</p></div>;
  }

  if (resetToken && !resetInfo && !error) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p>Loading password reset…</p></div>;
  }

  // -- MFA challenge view --
  if (mfaPendingToken) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'Inter, sans-serif' }}>
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '36px 32px', width: '100%', maxWidth: 420 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, marginTop: 0, marginBottom: 6 }}>Two-factor verification</h1>
          <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 20 }}>
            {useBackupCode
              ? 'Enter one of the backup codes you saved during enrolment.'
             : 'Open your authenticator app and enter the 6-digit code shown for eyez ON YOU.'}
          </p>
          <form onSubmit={submitMFA}>
            <input
              type="text"
              inputMode={useBackupCode ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              autoFocus
              placeholder={useBackupCode ? 'XXXX-XXXX' : '000000'}
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value)}
              required
              maxLength={useBackupCode ? 9 : 6}
              style={{ ...inp, fontFamily: 'monospace', fontSize: 18, letterSpacing: 4, textAlign: 'center' }}
            />
            {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <button type="submit" disabled={loading || !mfaCode} style={{ width: '100%', background: loading ? '#9CA3AF' : platform.primary_colour, color: '#fff', border: 'none', borderRadius: 9, padding: '11px', fontWeight: 600, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Verifying…' : 'Verify'}
            </button>
          </form>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, fontSize: 12 }}>
            <button onClick={() => { setUseBackupCode(!useBackupCode); setMfaCode(''); setError(null); }}
              style={{ background: 'transparent', border: 'none', color: '#2563EB', cursor: 'pointer', padding: 0, fontSize: 12 }}>
              {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
            </button>
            <button onClick={cancelMFA}
              style={{ background: 'transparent', border: 'none', color: '#6B7280', cursor: 'pointer', padding: 0, fontSize: 12 }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Forced 2FA enrollment gate
  if (enrolToken) {
    return <MfaEnrollmentGate enrolToken={enrolToken} primaryColour={platform.primary_colour} onDone={onAuth} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'Inter, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '36px 32px', width: '100%', maxWidth: 420 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          {platform.has_logo
            ? <img src={`${API}/platform-settings/logo`} alt={platform.system_name} style={{ height: 32, maxWidth: 120, objectFit: 'contain' }} />
            : <div style={{ width: 32, height: 32, borderRadius: 7, background: platform.primary_colour, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{(platform.system_name || '').charAt(0)}</span>
              </div>}
          <span style={{ fontWeight: 600, fontSize: 16 }}>{platform.system_name}</span>
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 600, marginTop: 20, marginBottom: 4 }}>
          {inviteInfo ? 'Accept invitation' : resetInfo ? 'Set a new password' : 'Sign in'}
        </h1>
        <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 20 }}>
          {inviteInfo
            ? `You've been invited to ${inviteInfo.company_name || 'the platform'} as ${inviteInfo.role.replace('_', ' ')}. Set a password to continue.`
            : resetInfo
              ? `Resetting password for ${resetInfo.user_email}. Enter a new password to continue. After saving you'll be signed in automatically.`
              : 'Enter your credentials to continue. Accounts are created by invitation only.'}
        </p>

        <form onSubmit={submit}>
          {inviteInfo ? <>
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={inviteInfo.email}
              disabled
              style={{ ...inp, background: '#F8F9FB' }}
            />
            <input
              type="text"
              name="name"
              autoComplete="name"
              placeholder="Full name"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              style={inp}
            />
            <input
              type="password"
              name="new-password"
              autoComplete="new-password"
              placeholder="Set a password (min 8 chars)"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={8}
              style={inp}
            />
          </> : resetInfo ? <>
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={resetInfo.user_email}
              disabled
              style={{ ...inp, background: '#F8F9FB' }}
            />
            <input
              type="password"
              name="new-password"
              autoComplete="new-password"
              placeholder="New password (min 8 chars)"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={8}
              autoFocus
              style={inp}
            />
          </> : <>
            <input
              type="email"
              name="email"
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={inp}
            />
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={inp}
            />
          </>}

          {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

          <button type="submit" disabled={loading} style={{ width: '100%', background: loading ? '#9CA3AF' : platform.primary_colour, color: '#fff', border: 'none', borderRadius: 9, padding: '11px', fontWeight: 600, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Please wait…' : (inviteInfo ? 'Accept and continue' : resetInfo ? 'Set new password' : 'Sign in')}
          </button>
        </form>

        {!inviteInfo && <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#9CA3AF' }}>Need access? Contact your administrator.</p>}

        {platform.privacy_notice && (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #F3F4F6', fontSize: 11, color: '#9CA3AF', lineHeight: 1.5 }}>
            <strong style={{ color: '#6B7280' }}>Privacy notice:</strong> {platform.privacy_notice}
          </div>
        )}
      </div>
    </div>
  );
}
